/**
 * Declarations (rules §5), detected by the engine rather than announced
 * (ARCHITECTURE D11): a digital table knows every holding, so there is nothing
 * to misdeclare and no renonce to punish.
 *
 * Each seat's eight cards are searched for carrés first, then sequences among
 * what is left — a card counts towards one combination only, except that the
 * trump King and Queen also make Belote. Only the side holding the single best
 * combination scores, and it then scores all of its combinations.
 */
import { SUITS, rankOf, suitOf } from '../../core/cards.js';
const CARRE_POINTS = { 11: 200, 9: 150, 14: 100, 10: 100, 13: 100, 12: 100 };
const RUN_POINTS = {
    3: { kind: 'tierce', points: 20 },
    4: { kind: 'quarte', points: 50 },
    5: { kind: 'quinte', points: 100 },
};
export function findDeclarations(seat, cards) {
    const out = [];
    const used = new Set();
    // Carrés of 7s and 8s are worth nothing and do not declare (edge #7).
    for (const rank of [11, 9, 14, 10, 13, 12]) {
        const four = cards.filter((c) => rankOf(c) === rank);
        if (four.length === 4) {
            out.push({ seat, kind: 'carre', cards: four, points: CARRE_POINTS[rank] });
            for (const c of four)
                used.add(c);
        }
    }
    // Sequences run in natural order 7 8 9 10 J Q K A in every suit — which is
    // rank order here. Runs past five are still worth 100 (edge #16).
    for (const suit of SUITS) {
        const ranks = cards
            .filter((c) => suitOf(c) === suit && !used.has(c))
            .map((c) => rankOf(c) ?? 0)
            .sort((a, b) => a - b);
        let start = 0;
        for (let i = 1; i <= ranks.length; i++) {
            if (i < ranks.length && ranks[i] === ranks[i - 1] + 1)
                continue;
            const run = ranks.slice(start, i);
            if (run.length >= 3) {
                const top = run.slice(-Math.min(run.length, 5));
                const { kind, points } = RUN_POINTS[Math.min(run.length, 5)];
                out.push({ seat, kind, points, cards: top.map((r) => cards.find((c) => rankOf(c) === r && suitOf(c) === suit)) });
            }
            start = i;
        }
    }
    return out;
}
function topRank(d) {
    return Math.max(...d.cards.map((c) => rankOf(c) ?? 0));
}
/**
 * Rules §5.4, in order: more points; at equal points a carré beats a sequence;
 * then the higher top card; then a trump sequence over a plain one; then the
 * seat earliest in play from the dealer's right. Positive when `a` is better.
 */
export function compareDeclarations(a, b, trump, dealer, seatCount) {
    if (a.points !== b.points)
        return a.points - b.points;
    const carre = (d) => (d.kind === 'carre' ? 1 : 0);
    if (carre(a) !== carre(b))
        return carre(a) - carre(b);
    if (topRank(a) !== topRank(b))
        return topRank(a) - topRank(b);
    const inTrump = (d) => (d.kind !== 'carre' && suitOf(d.cards[0]) === trump ? 1 : 0);
    if (inTrump(a) !== inTrump(b))
        return inTrump(a) - inTrump(b);
    const order = (s) => (s - dealer - 1 + seatCount) % seatCount;
    return order(b.seat) - order(a.seat);
}
/** `cardsBySeat` is each seat's full eight-card hand. */
export function resolveDeclarations(cardsBySeat, trump, dealer, table) {
    const all = cardsBySeat.flatMap((cards, seat) => findDeclarations(seat, cards));
    if (all.length === 0)
        return { team: null, declarations: [], points: 0 };
    const best = all.reduce((a, b) => (compareDeclarations(b, a, trump, dealer, table.seatCount) > 0 ? b : a));
    const team = table.teamOf[best.seat];
    const declarations = all.filter((d) => table.teamOf[d.seat] === team);
    return { team, declarations, points: declarations.reduce((n, d) => n + d.points, 0) };
}
/** The seat holding both the King and Queen of trumps, if any. */
export function beloteHolder(cardsBySeat, trump) {
    const seat = cardsBySeat.findIndex((cards) => cards.includes(`13${trump}`) && cards.includes(`12${trump}`));
    return seat === -1 ? null : seat;
}
/** Each seat's eight cards as dealt, from what it played plus what it holds. */
export function cardsDealt(tricks, holdings) {
    const out = holdings.map((h) => [...h]);
    for (const t of tricks)
        for (const p of t.plays)
            out[p.seat]?.push(p.card);
    return out;
}
//# sourceMappingURL=declarations.js.map